import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import FixaSSRIcon from "@coworker/apprestructured/src/shared/wrappers/FixaSSRIcon/FixaSSRIcon";
import TrashCanIconPath from "@coworker/apprestructured/src/shared/wrappers/FixaSSRIcon/paths/TrashCanIconPath";
import RotateObjectLeftIconPath from "@coworker/apprestructured/src/shared/wrappers/FixaSSRIcon/paths/RotateObjectLeftIconPath";
import HandleIconPath from "@coworker/apprestructured/src/shared/wrappers/FixaSSRIcon/paths/HandleIconPath";
import FixaButton from "@coworker/apprestructured/src/shared/wrappers/FixaButton/FixaButton";
import styles from "./SortableItem.module.css";
import { FixaText } from "@coworker/apprestructured/src/shared/wrappers/FixaText/FixaText";
import {
  createNewOrExistingFileKey,
  NewOrExistingFile,
  useNewOrExistingFileUrl,
} from "@coworker/apprestructured/src/shared/types/newOrExistingFile";

interface SortableItemProps {
  image: NewOrExistingFile;
  images: NewOrExistingFile[];
  onRemove: (image: NewOrExistingFile) => void;
  onRotate: (image: NewOrExistingFile) => void;
}

export default function SortableItem({
  image,
  images,
  onRemove,
  onRotate,
}: SortableItemProps) {
  const key = createNewOrExistingFileKey(image);
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: key });

  const src = useNewOrExistingFileUrl(image);
  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
      }}
      {...attributes}
      className={styles["imageRow"]}
    >
      <div className={styles["imageRowInner"]}>
        {images.length > 1 && (
          <div {...listeners} className={styles["dragHandle"]} tabIndex={0}>
            <FixaSSRIcon paths={HandleIconPath} />
          </div>
        )}
        <div>
          <img
            className={styles["image"]}
            style={{ transform: `rotate(${image.rotation}deg)` }}
            src={src}
            alt={image.file?.name}
          />
        </div>
        <FixaText textStyle="Body.M" className={styles["imageName"]}>
          {image.file?.name}
        </FixaText>
        <div className={styles["buttonGroup"]}>
          <FixaButton
            iconOnly
            ssrIcon={RotateObjectLeftIconPath}
            onClick={() => onRotate(image)}
            size="small"
            data-testid="update-image-button"
            aria-label="Rotate image"
          />
          <FixaButton
            iconOnly
            ssrIcon={TrashCanIconPath}
            onClick={() => onRemove(image)}
            size="small"
            data-testid="remove-image-button"
            aria-label="Remove image"
          />
        </div>
      </div>
    </div>
  );
}
